import {
  ref,
  onMounted,
  onBeforeUnmount,
  defineComponent,
  h
} from 'vue'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift
} from '@floating-ui/vue'
import DropdownContentVue from './DropdownContent.vue'

/**
 * Dropdown composable that returns controls and a bound content component.
 *
 * @param {Ref<HTMLElement>} triggerRef - Ref to the trigger element
 * @param {Object} options - Configuration options
 * @param {string} options.placement - Floating UI placement (default: 'bottom-start')
 * @param {number} options.offset - Offset from trigger in pixels (default: 4)
 * @param {boolean} options.closeOnEscape - Close when pressing Escape (default: true)
 *
 * @returns {Object} { isOpen, toggle, open, close, DropdownContent }
 *
 * @example
 * const triggerRef = ref(null)
 * const { DropdownContent, toggle } = useDropdown(triggerRef)
 *
 * <button ref="triggerRef" @click="toggle">Menu</button>
 * <DropdownContent>
 *   <div class="app-dropdown__item">Item</div>
 * </DropdownContent>
 */
export default function useDropdown(triggerRef, options = {}) {
  const {
    placement = 'bottom-start',
    offset: offsetValue = 4,
    closeOnEscape = true
  } = options

  const isOpen = ref(false)
  const contentRef = ref(null)

  const { floatingStyles } = useFloating(triggerRef, contentRef, {
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetValue),
      flip(),
      shift({ padding: 8 })
    ]
  })

  const toggle = () => {
    isOpen.value = !isOpen.value
  }

  const open = () => {
    isOpen.value = true
  }

  const close = () => {
    isOpen.value = false
  }

  const handleClickOutside = (event) => {
    if (!isOpen.value) return

    const clickedTrigger = triggerRef.value?.contains(event.target)
    const clickedContent = contentRef.value?.contains(event.target)

    if (!clickedTrigger && !clickedContent) {
      close()
    }
  }

  const handleKeydown = (event) => {
    if (closeOnEscape && event.key === 'Escape' && isOpen.value) {
      close()
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', handleKeydown)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside)
    document.removeEventListener('keydown', handleKeydown)
  })

  const DropdownContent = defineComponent({
    name: 'DropdownContent',
    setup(_, { slots }) {
      return () => h(DropdownContentVue, {
        isOpen: isOpen.value,
        floatingStyles: floatingStyles.value,
        close,
        'onUpdate:contentRef': (el) => { contentRef.value = el }
      }, slots)
    }
  })

  return {
    isOpen,
    toggle,
    open,
    close,
    DropdownContent
  }
}
